/* eslint-disable react-hooks/exhaustive-deps */
import Profile from "../components/Profile";
import {
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Portal,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useMediaQuery,
  VStack
} from "@chakra-ui/react";
import {ItemsGrid} from "../components/ItemsGrid";
import {FabButton} from "../components/FabButton";
import {ShareDialogue} from "../components/ShareDialogue";
import AddItemModal from "../components/AddItemModal";
import {useEffect, useState} from "react";
import {ItemType} from "../services/types";
import EditProfileModal from "../components/EditProfileModal";
import {Link as RouterLink, useLocation, useNavigate} from "react-router-dom";
import {useUIManager} from "../hooks/useUIManager";
import {useAPI} from "../hooks/useAPI";
import {useBookingActions} from "../hooks/useBookingActions";
import {usePendingBooking} from "../hooks/usePendingBooking";
import {observer} from "mobx-react-lite";
import {UserResponse} from "../services/responses";
import EditItemModal from "../components/EditItemModal";
import ListModal from "../components/ListModal";
import ListsTab from "../components/ListsTab";
import {GiSadCrab} from "react-icons/gi";

const ProfilePage = () => {
  const uiManager = useUIManager();
  const location = useLocation();
  const navigate = useNavigate();
  const api = useAPI();

  const [isMd] = useMediaQuery("(min-width: 576px)");
  const [loaded, setLoaded] = useState(false);
  const [items, setItems] = useState<ItemType[]>([]);

  const isOwnProfilePath = () => api.loggedIn && (location.pathname.startsWith("/profile") || location.pathname === `/${api.loggedInUser?.username}`);
  const [isOwnProfile, setIsOwnProfile] = useState(isOwnProfilePath());
  const [user, setUser] = useState<UserResponse | null>(null)

  const {book, unbook} = useBookingActions(setItems);
  usePendingBooking(loaded, items, book);

  useEffect(() => {
    // on the profile page the add/edit item modals target the main wishlist, not a list
    uiManager.setCurrentList(null);
    api.checkAuthorization()
      .then((loggedIn: boolean) => {
        if (!loggedIn && location.pathname.startsWith("/profile")) {
          return null;
        }

        if (location.pathname.startsWith("/profile") && uiManager.needReloadProfile) {
          return api.getUser(api.loggedInUser?.username || "");
        }

        if (location.pathname.startsWith("/profile")) {
          return api.loggedInUser;
        }

        return api.getUser(location.pathname.substring(1));
      })
      .then((user) => {
        const isOwn = isOwnProfilePath();
        setUser(user);
        setIsOwnProfile(isOwn);

        if (!user) {
          if (location.pathname.startsWith("/profile")) {
            navigate("/signin");
            return user;
          }

          return user;
        }

        if (location.pathname === '/profile/edit' && !uiManager.editModalShown) {
          uiManager.toggleEditModal(true);
        }

        return user;
      })
      .then((user) => {
        return api.getItems(user === null || isOwnProfile ? undefined : user?.username)
      })
      .then((items_) => {
          setLoaded(true);
          setItems(items_)
        }
      )
      .finally(() => {
        uiManager.toggleNeedReloadItems(false);
      })
  }, [uiManager.needReloadItems, uiManager.needReloadProfile, location.pathname]);

  return <>
    {loaded && user === null && (
      <>
        <VStack gap={2}>
          <Icon as={GiSadCrab} boxSize="64px"/>
          <Heading textAlign="center">Page not found</Heading>
          <Text textAlign="center" fontSize="lg" textColor="gray.500">Oh no, we don't seem to have the page you're
            looking for.</Text>
          <Button as={RouterLink} to="/">Return to homepage</Button>
        </VStack>
      </>
    )}

    {loaded && user !== null && (<>
      <Profile isOwnPage={isOwnProfile} user={user}/>

      <Tabs mt={4} colorScheme="brand" isLazy>
        <TabList>
          <Tab>Wishlist</Tab>
          <Tab>Lists</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            {!!items.length && (
              <>
                <ItemsGrid items={items} setItems={setItems} editable={isOwnProfile}
                           persistSorting={(uuids) => api.updateSorting(uuids)}
                           booking={{
                             enabled: !!user.allow_booking,
                             isOwner: isOwnProfile,
                             onBook: book,
                             onUnbook: unbook,
                           }}/>
                {isOwnProfile && (<ShareDialogue url={`https://wishlists.cc/${user?.username}`}/>)}
              </>
            )}
            {!items.length && (
              <Box textAlign="center" mt="3rem">
                <VStack color="gray.500" fontSize={{base: "xs", md: "sm"}} gap={0}>
                  <Text fontSize={{base: "36px", md: "48px"}}>¯\_(ツ)_/¯</Text>
                  <Text>{isOwnProfile ? 'Your' : 'Their'} wishlist is empty.</Text>
                  {isOwnProfile && <Text>It's time to add something to it.</Text>}
                </VStack>
              </Box>
            )}
          </TabPanel>
          <TabPanel px={0}>
            <ListsTab username={user.username} isOwnProfile={isOwnProfile}/>
          </TabPanel>
        </TabPanels>
      </Tabs>
      {isOwnProfile && !isMd && <FabButton/>}
    </>)}

    {!loaded && (<>
      <Flex justifyContent="center" alignItems="center" flexGrow={1} mt="3rem">
        <Spinner size="xl"/>
      </Flex>
    </>)}

    <Portal>
      <AddItemModal/>
      <EditItemModal/>
      <EditProfileModal/>
      <ListModal/>
    </Portal>
  </>
}

export default observer(ProfilePage)
